import { useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useSurveyStore } from '@/stores/survey-store';
import { useSessionStore } from '@/stores/session-store';
import { useNavigationStore } from '@/stores/navigation-store';
import { useUIStore } from '@/stores/ui-store';
import { SurveyLayout } from '@/components/layout/survey-layout';
import { LoadingScreen } from '@/components/screens/loading-screen';
import { ErrorScreen } from '@/components/screens/error-screen';
import { CompleteScreen } from '@/components/screens/complete-screen';
import { ScreenoutScreen } from '@/components/screens/screenout-screen';
import { OverquotaScreen } from '@/components/screens/overquota-screen';
import { TerminalScreen } from '@/components/screens/terminal-screen';

export function SurveyPage() {
  const { surveyId } = useParams<{ surveyId: string }>();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('sid');
  // Optional respondent locale; strict-matched against enabled locales BE-side.
  const locale = searchParams.get('locale') ?? undefined;

  const { loadSurvey, isLoading: surveyLoading, survey } = useSurveyStore();
  const { initSession, start, session, isActive, terminal } = useSessionStore();
  const { isLoading, error, setError } = useUIStore();

  // Initialize survey and session
  useEffect(() => {
    async function initialize() {
      if (!surveyId) {
        setError('Survey ID is required');
        return;
      }

      if (!sessionId) {
        setError('Session ID is required. Please use a valid survey link.');
        return;
      }

      try {
        // Prime local session state, then ACTIVATE the session server-side
        // (marks startedAt + confirms it's still active) — without this the
        // page never leaves the loading fallback because `isActive` stays
        // false. `start()` 400s on a consumed/terminated session, which the
        // catch surfaces as an error screen rather than an endless spinner.
        await initSession(sessionId);
        await start();
        await loadSurvey(surveyId, sessionId, locale);

        // Initialize page navigation from the loaded questions. Nothing else
        // sets totalPages/isLastPage (the useNavigation hook that would is not
        // mounted), so without this a single-page survey keeps isLastPage=false
        // and shows a dead "Next" (no next page → no-op) instead of "Submit".
        const loadedQuestions = useSurveyStore.getState().questions;
        const totalPages = loadedQuestions.length
          ? Math.max(...loadedQuestions.map((q) => q.page))
          : 1;
        useNavigationStore.getState().initialize(totalPages);
      } catch (err) {
        console.error('Failed to initialize survey:', err);
        setError(
          err instanceof Error ? err.message : 'Failed to load survey'
        );
      }
    }

    initialize();
  }, [surveyId, sessionId, locale, loadSurvey, initSession, start, setError]);

  // Show loading screen
  if (isLoading || surveyLoading) {
    return <LoadingScreen />;
  }

  // Show error screen
  if (error) {
    return <ErrorScreen message={error} />;
  }

  // A BE-pushed terminal (overquota/timeout/etc) takes precedence — it carries
  // the authoritative redirect URL and may arrive before any status change.
  if (terminal) {
    return (
      <TerminalScreen
        outcome={terminal.outcome}
        returnUrl={terminal.redirectUrl}
      />
    );
  }

  // Check session status for end screens
  if (session) {
    if (session.status === 'completed') {
      return <CompleteScreen returnUrl={session.returnUrlComplete} />;
    }

    if (session.status === 'terminated') {
      if (session.outcome === 'screenout') {
        return <ScreenoutScreen returnUrl={session.returnUrlScreenout} />;
      }
      if (session.outcome === 'overquota') {
        return <OverquotaScreen returnUrl={session.returnUrlOverquota} />;
      }
    }
  }

  // Show survey layout with questions
  if (survey && isActive) {
    return <SurveyLayout />;
  }

  // Fallback loading
  return <LoadingScreen />;
}
